// Ekran 06 — saatlik beyan gridi (table.bey).
//
// 26 sütun: sticky "Ünite / Parametre" + 24 saat + Toplam.
// Hücre sınıfları referanstan birebir:
//   .off   → sıklık o saatte hücre AÇMIYOR (periyot dışı, "—")
//   .miss  → NOW öncesi boş (K30 amber)
//   .es1   → eşik dışı · izle     .es2 → eşik dışı · kritik
//   .calcc → hesaplanan satır (dengesizlik); .amb / .red bantları

import { useEffect, useState } from 'react'
import { tr, sayiyaCevir } from '../../lib/trFormat'
import { trGiris } from '../../lib/trFormat'

import type {
  BeyanGunuDto, BeyanSatiri, BeyanUnitesi, HucreDto, OlayTipi, TesisSatiri,
} from './api'
import { gazSatiri, olayKayitlari, ortalama } from './hesap'

const SAATLER = Array.from({ length: 24 }, (_, i) => i)

/**
 * Ünite başlığının alt etiketi (`uh-sub`) GRUP adını yazar — referansın `sub`
 * alanı böyle: "Çürütme" (ref:450), "Gaz Hattı" (ref:461), "Enerji · Bakımda"
 * (ref:478). Ünite adı zaten başlıkta duruyor; oraya ikinci kez yazılınca
 * "CR-1 — Ana Çürütücü  Ana Çürütücü" çıkıyordu.
 * Karşılıklar §5.1 `unite_sinifi.grup` enum'undan.
 */
const GRUP_ADI: Record<BeyanUnitesi['grup'], string> = {
  on: 'Ön İşlem',
  cur: 'Çürütme',
  depo: 'Depo',
  gaz: 'Gaz Hattı',
  enerji: 'Enerji',
}

/**
 * ref:645 · 669-674 — "İlk değerle satırı doldur". Satırın ilk dolu değeri
 * YALNIZ boş hücrelere yazılır; dolu hücreye dokunulmaz.
 */
function DoldurDugmesi({ tikla }: { tikla: () => void }) {
  return (
    <button
      className="rf-btn"
      type="button"
      title="İlk değerle satırı doldur"
      onClick={tikla}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </button>
  )
}

/**
 * ref:645 — K28 "⟳": TESİS satırını ünitelerden hesaplar, sonuç üzerine
 * yazılabilir. Glif yerine referansın SVG'si: `.rf-btn` 17×17 bir kutu ve
 * `⟳` karakteri o kutuda `DoldurDugmesi`nin okundan farklı optik ağırlıkta
 * oturuyordu.
 */
function HesaplaDugmesi({ tikla }: { tikla: () => void }) {
  return (
    <button
      className="rf-btn"
      type="button"
      title="Ünite verilerinden hesapla — üzerine yazılabilir"
      onClick={tikla}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-3-6.7M21 3v6h-6" />
      </svg>
    </button>
  )
}

/**
 * TESİS bloğunun bir VERİ satırı (K28). Referans sırası Dengesizlik'i Satış ile
 * Gaz tüketimi ARASINA koyduğu (ref:443-449) için satırlar iki parçada
 * basılıyor; gövde iki yerde kopyalanmasın diye buraya çıkarıldı.
 */
function TesisVeriSatiri({
  tesis,
  kilitli,
  tesisYaz,
  hesapla,
  tesisDoldur,
}: {
  tesis: TesisSatiri
  kilitli: boolean
  tesisYaz: (tip: TesisSatiri['tip'], saat: number, deger: number | null) => void
  hesapla: (tip: TesisSatiri['tip']) => void
  tesisDoldur: (tesis: TesisSatiri) => void
}) {
  return (
    <tr>
      <td className="pl">
        {tesis.etiket}
        <span className="bpu2">{tesis.birim} · saatlik</span>
        {!kilitli && tesis.hesaplanabilir && <HesaplaDugmesi tikla={() => hesapla(tesis.tip)} />}
        {!kilitli && <DoldurDugmesi tikla={() => tesisDoldur(tesis)} />}
      </td>
      {SAATLER.map((s) => (
        <Hucre
          key={s}
          hucre={tesis.hucreler[s]}
          kilitli={kilitli}
          basamak={0}
          yaz={(d) => tesisYaz(tesis.tip, s, d)}
        />
      ))}
      {/* ref:645 — VERİ satırının toplamı 600; 700 yalnız calc satırlarında. */}
      <td className="mono" style={{ fontWeight: 600 }}>{tr(tesis.toplam, 0)}</td>
    </tr>
  )
}

/** Hücrenin td sınıfı — referans `cls` mantığı. */
function hucreSinifi(h: HucreDto | null, kilitli: boolean): string {
  if (h === null) return 'off'
  if (!kilitli && h.durum === 'eksik') return 'miss'
  if (h.bant === 'kritik') return 'es2'
  if (h.bant === 'izle') return 'es1'
  return ''
}

/**
 * Tek hücre. Değer yerel durumda tutulur; blur/Enter'da sunucuya yazılır —
 * her tuşta istek atmak 24×43 hücrelik bir gridde kabul edilemez.
 */
function Hucre({
  hucre,
  kilitli,
  basamak,
  yaz,
}: {
  hucre: HucreDto | null
  kilitli: boolean
  basamak: number
  yaz: (deger: number | null) => void
}) {
  const [taslak, setTaslak] = useState('')
  const [duzenleniyor, setDuzenleniyor] = useState(false)

  useEffect(() => {
    if (!duzenleniyor) setTaslak(hucre?.deger == null ? '' : trGiris(hucre.deger))
  }, [hucre?.deger, duzenleniyor])

  if (hucre === null) return <td className="off">—</td>

  function bitir() {
    setDuzenleniyor(false)
    const yeni = taslak.trim() === '' ? null : sayiyaCevir(taslak)
    if (yeni !== (hucre?.deger ?? null)) yaz(yeni)
  }

  return (
    <td className={hucreSinifi(hucre, kilitli)}>
      <input
        value={duzenleniyor ? taslak : hucre.deger == null ? '' : tr(hucre.deger, basamak)}
        disabled={kilitli}
        onFocus={() => {
          setDuzenleniyor(true)
          setTaslak(hucre.deger == null ? '' : trGiris(hucre.deger))
        }}
        onChange={(e) => setTaslak(e.target.value)}
        onBlur={bitir}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') { setDuzenleniyor(false); e.currentTarget.blur() }
        }}
      />
    </td>
  )
}

export default function BeyanGridi({
  gun,
  seciliUnite,
  yazabilir,
  degerYaz,
  tesisYaz,
  hesapla,
  satirDoldur,
  tesisDoldur,
  olayEkle,
}: {
  gun: BeyanGunuDto
  /** "tumu" ya da ünite kimliği · "TESIS" TESİS bloğunu tek başına gösterir. */
  seciliUnite: string
  yazabilir: boolean
  degerYaz: (parametreId: string, saat: number, deger: number | null) => void
  tesisYaz: (tip: TesisSatiri['tip'], saat: number, deger: number | null) => void
  hesapla: (tip: TesisSatiri['tip']) => void
  /** ref:645 → satırı ilk değeriyle doldur (yalnız boş hücreler). */
  satirDoldur: (satir: BeyanSatiri) => void
  tesisDoldur: (tesis: TesisSatiri) => void
  /** K23: olay satırının "+ kayıt" düğmesi — tip ve ünite ön seçili açılır. */
  olayEkle: (tip: OlayTipi, uniteId: string) => void
}) {
  const kilitli = gun.durum === 'onayli' || !yazabilir
  const tesisGoster = seciliUnite === 'tumu' || seciliUnite === 'TESIS'

  // ref:443-449 satır sırası: üretim · iç ihtiyaç · satış → Dengesizlik ± →
  // gaz tüketimi → Ort. metan. Sunucu satırları enum sırasında yolluyor, bölme
  // burada yapılıyor. Taahhüt (K41) referansta yok; sapmasıyla birlikte sonda.
  const tesisOnce = gun.tesis.filter((t) => t.tip !== 'gaz_tuketim' && t.tip !== 'taahhut')
  const tesisSonra = gun.tesis.filter((t) => t.tip === 'gaz_tuketim' || t.tip === 'taahhut')

  return (
    <div className="bey-wrap">
      {/*
        `data-simdi` = içinde bulunulan saat (yalnız BUGÜN görüntülenirken;
        sunucudan geliyor, §12). durumlar.css o sütunun başlığını ve
        hücrelerini koyu kırmızı çerçeveyle işaretliyor — operatör 24 sütunluk
        gridde "şimdi hangi veri setini gireceğim" sorusunu gözle çözemiyordu.

        İşaret sütun bazlı bir CSS kuralı (nth-child) ile veriliyor, hücrelere
        tek tek öznitelik basılmıyor: hücreler altı ayrı yerde (Hucre,
        TesisHucre, HesapHucre, düz td, th, hesaplanan metan satırı)
        üretiliyor ve hepsine ayrı ayrı geçirmek satır tiplerinden birini
        atlamaya açık olurdu. Sütun kuralı yeni satır tipini de kendiliğinden
        kapsıyor.
      */}
      <table className="bey" data-simdi={gun.aktifSaat ?? undefined}>
        <thead>
          <tr>
            <th className="pl">Ünite / Parametre</th>
            {SAATLER.map((s) => (
              <th key={s}>{String(s).padStart(2, '0')}</th>
            ))}
            <th style={{ minWidth: 64 }}>Toplam</th>
          </tr>
        </thead>
        <tbody>
          {/* ---- K28: TESİS bloğu, gridin ilk grubu ---- */}
          {tesisGoster && (
            <>
              <tr className="u-head">
                <td colSpan={26}>
                  TESİS — Tesis Geneli
                  <span className="uh-sub">Bağımsız beyan — resmi değerler (K28)</span>
                </td>
              </tr>

              {tesisOnce.map((t) => (
                <TesisVeriSatiri
                  key={t.tip}
                  tesis={t}
                  kilitli={kilitli}
                  tesisYaz={tesisYaz}
                  hesapla={hesapla}
                  tesisDoldur={tesisDoldur}
                />
              ))}

              {/* K29 dengesizlik — hesaplanan satır, salt-okunur */}
              <tr>
                <td className="pl" style={{ color: 'var(--navy-700)' }}>
                  Dengesizlik ±<span className="bpu2">kWh · hesaplanan</span>
                </td>
                {SAATLER.map((s) => (
                  <DengesizlikHucresi
                    key={s}
                    deger={gun.dengesizlik[s]}
                    bant={gun.dengesizlikBant[s]}
                  />
                ))}
                <td className="mono" style={{ fontWeight: 700 }}>
                  {tr(gun.dengesizlik.reduce<number | null>(
                    (a, d) => (d === null ? a : (a ?? 0) + d), null), 0)}
                </td>
              </tr>

              {tesisSonra.map((t) => (
                <TesisVeriSatiri
                  key={t.tip}
                  tesis={t}
                  kilitli={kilitli}
                  tesisYaz={tesisYaz}
                  hesapla={hesapla}
                  tesisDoldur={tesisDoldur}
                />
              ))}

              {/* K41 taahhüt sapması */}
              <tr>
                <td className="pl" style={{ color: 'var(--navy-700)' }}>
                  Taahhüt sapması<span className="bpu2">kWh · K41</span>
                </td>
                {SAATLER.map((s) => (
                  <DengesizlikHucresi
                    key={s}
                    deger={gun.taahhutSapmasi[s]}
                  />
                ))}
                <td className="mono" style={{ fontWeight: 700 }}>
                  {tr(gun.taahhutSapmasi.reduce<number | null>(
                    (a, d) => (d === null ? a : (a ?? 0) + d), null), 0)}
                </td>
              </tr>

              {/* ref:449 · 625-626 — GA-1 CH₄ ölçümünün salt-okunur aynası;
                  ayrı veri tutmaz. Referans hücreleri `hh%4` ile SABİT kodluyor;
                  burada kaynak satırın kendi null deseni kullanılıyor, çünkü
                  sıklık Üniteler ekranından değiştirilebiliyor. */}
              <OrtMetanSatiri gun={gun} />
            </>
          )}

          {/* ---- ünite blokları ---- */}
          {gun.uniteler
            .filter((u) => seciliUnite === 'tumu' || u.uniteId === seciliUnite)
            .map((u) => (
              <UniteBlogu
                key={u.uniteId}
                gun={gun}
                unite={u}
                kilitli={kilitli}
                degerYaz={degerYaz}
                satirDoldur={satirDoldur}
                olayEkle={olayEkle}
              />
            ))}
        </tbody>
      </table>
    </div>
  )
}

function UniteBlogu({
  gun,
  unite,
  kilitli,
  degerYaz,
  satirDoldur,
  olayEkle,
}: {
  gun: BeyanGunuDto
  unite: BeyanGunuDto['uniteler'][number]
  kilitli: boolean
  degerYaz: (parametreId: string, saat: number, deger: number | null) => void
  satirDoldur: (satir: BeyanSatiri) => void
  olayEkle: (tip: OlayTipi, uniteId: string) => void
}) {
  return (
    <>
      <tr className="u-head">
        <td colSpan={26}>
          {unite.kod} — {unite.ad}
          <span className="uh-sub">
            {unite.durum === 'bakimda'
              ? `${GRUP_ADI[unite.grup]} · Bakımda`
              : GRUP_ADI[unite.grup]}
          </span>
        </td>
      </tr>
      {unite.satirlar.map((r) => (
        <Satir
          key={r.parametreId}
          gun={gun}
          unite={unite}
          satir={r}
          kilitli={kilitli}
          degerYaz={degerYaz}
          satirDoldur={satirDoldur}
          olayEkle={olayEkle}
        />
      ))}
    </>
  )
}

function Satir({
  gun,
  unite,
  satir,
  kilitli,
  degerYaz,
  satirDoldur,
  olayEkle,
}: {
  gun: BeyanGunuDto
  unite: BeyanGunuDto['uniteler'][number]
  satir: BeyanSatiri
  kilitli: boolean
  degerYaz: (parametreId: string, saat: number, deger: number | null) => void
  satirDoldur: (satir: BeyanSatiri) => void
  olayEkle: (tip: OlayTipi, uniteId: string) => void
}) {
  // Basamak SATIRIN kendi tanımından gelir (ref:642 `tr(v, r.d||0)`); birimden
  // tahmin edilemiyordu — aynı birimin ("%") referansta iki farklı basamağı var.
  const basamak = satir.basamak

  // K21: lab kaynaklı satır beyanda salt-okunur akar — tek geniş hücre.
  // colSpan 24 + kapanış "Toplam" hücresi: satır 26 sütuna oturmazsa gridin
  // son sütunu bu satırlarda kayıyordu (ref 634).
  if (satir.kaynak === 'lab') {
    return (
      <tr>
        <td className="pl">
          {satir.ad}
          <span className="bpu2">{satir.birim} · lab</span>
        </td>
        <td className="labc" colSpan={24}>
          Laboratuvardan salt-okunur akar (K21) — girilecek hücre yok
        </td>
        <td className="off">—</td>
      </tr>
    )
  }

  // Olay bazlı satır: zamanlanmış hücre yok, gün kayıtları çip olarak akar
  // ve "+ kayıt" düğmesi drawer'ı bu satırın tipiyle açar (ref 636).
  if (satir.frek === 'olay') {
    const kayitlar = olayKayitlari(gun, satir, unite.uniteId)
    return (
      <tr>
        <td className="pl">
          {satir.ad}
          <span className="bpu2">{satir.birim} · olay</span>
        </td>
        <td className="olayc" colSpan={24}>
          {kayitlar.map((o) => (
            <span className="evchip" key={o.id}>
              {String(o.saat).padStart(2, '0')}:00 · {tr(o.deger, 1)} {o.birim}
            </span>
          ))}
          {!kilitli && (
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              style={{ height: 19, padding: '0 7px', fontSize: 10 }}
              // Eşleşmeyen satırda tip bilinmiyor; uydurma tiple kayıt açmak
              // yerine düğme kapalı kalır (bkz. BeyanOkuma.OlayTipiEsle).
              // §9.2 şablonlarındaki olay parametrelerinin tamamı eşleşiyor;
              // kapalı dal yalnız elle eklenmiş özel adlar için kalıyor.
              disabled={satir.olayTipi === null}
              title={satir.olayTipi === null
                ? 'Bu satır için olay tipi eşleşmedi — kayıt açılamıyor'
                : undefined}
              onClick={() => satir.olayTipi && olayEkle(satir.olayTipi, unite.uniteId)}
            >
              + kayıt
            </button>
          )}
        </td>
        <td className="off">{kayitlar.length || '—'}</td>
      </tr>
    )
  }

  return (
    <tr>
      <td className="pl">
        {satir.ad}
        <span className="bpu2">
          {satir.birim} · {satir.frek === '1' ? 'saatlik' : `${satir.frek}s`}
        </span>
        {!kilitli && <DoldurDugmesi tikla={() => satirDoldur(satir)} />}
      </td>
      {SAATLER.map((s) => (
        <Hucre
          key={s}
          hucre={satir.hucreler[s]}
          kilitli={kilitli}
          basamak={basamak}
          yaz={(d) => degerYaz(satir.parametreId, s, d)}
        />
      ))}
      {/* ref:644: saatlik + agg satırlar TOPLAM, gerisi ORTALAMA gösterir —
          iki doluluk okumasını toplayıp "%155" yazmak birimi anlamsız kılıyordu.
          Hangi satırın hangisi olduğuna sunucu karar veriyor (BeyanOkuma).
          Ağırlık 600: referans 700'ü yalnız calc satırlarına veriyor (ref:632). */}
      <td className="mono" style={{ fontWeight: 600 }}>
        {tr(satir.toplamOrtalama ? satir.ortalama : satir.toplam, basamak)}
      </td>
    </tr>
  )
}

/**
 * ref:449 — TESİS bloğunun "Ort. metan (GA-1)" satırı: GA ünitesinin CH₄
 * ölçümünü salt-okunur yansıtır (input YOK), Toplam sütununda gün ORTALAMASI
 * durur (ref:632). GA ünitesi yoksa satır hiç basılmaz — boş satır yerine
 * yokluk (K32).
 */
function OrtMetanSatiri({ gun }: { gun: BeyanGunuDto }) {
  const ch4 = gazSatiri(gun, 'CH₄')
  if (!ch4) return null

  return (
    <tr>
      <td className="pl" style={{ color: 'var(--navy-700)' }}>
        Ort. metan (GA-1)<span className="bpu2">% · hesaplanan</span>
      </td>
      {SAATLER.map((s) => {
        const h = ch4.hucreler[s]
        if (h == null) return <td className="off" key={s}>—</td>
        return (
          <td className="calcc" key={s}>{h.deger == null ? '·' : tr(h.deger, 1)}</td>
        )
      })}
      <td className="mono" style={{ fontWeight: 700 }}>{tr(ortalama(ch4), 1)}</td>
    </tr>
  )
}

/**
 * Hesaplanan hücre (.calcc). Renk bandı SUNUCUDAN geliyor: eşik `ayar_esik`'te
 * (§6.3 dengesizlik 2/5) ve Ayarlar ekranından değiştirilebiliyor. Burada sabit
 * yazılıyken eşik değişimi gride hiç yansımıyordu (§8-14 ihlali).
 *
 * Bandı olmayan hücre (taahhüt sapması) renksiz kalır — uydurma eşik
 * uygulanmıyor.
 */
function DengesizlikHucresi({ deger, bant }: { deger: number | null; bant?: string | null }) {
  if (deger === null) return <td className="calcc">·</td>

  const sinif = bant === 'kritik' ? 'calcc red' : bant === 'izle' ? 'calcc amb' : 'calcc'

  return (
    <td className={sinif}>
      {deger > 0 ? '+' : ''}
      {tr(deger, 0)}
    </td>
  )
}
